import './index.scss';

import React, {useContext, useMemo, useRef, useState} from "react";
import PropTypes from 'prop-types';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCaretDown} from "@fortawesome/free-solid-svg-icons";
import {
	DndContext,
	closestCenter,
	PointerSensor,
	useSensor,
	useSensors,
	DragOverlay,
} from '@dnd-kit/core';
import {
	SortableContext,
	useSortable,
	rectSortingStrategy,
	arrayMove,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import {isSafeLinkUrl} from '../../linkUrl';
import {getLinkImageDisplayUrl} from '../../favicon';
import {
	calculateNextIdxForProfile,
	findCategoryRenameCollisionTarget,
	formatCategoryName,
	getCategoryIdxForProfile,
	getFormattedValue,
	getLinksByCategories,
	handleAddCategory,
	mergeCategoryOnRenameCollision,
	normalizeCategoryNameForComparison,
	padCategories,
	sortCategories,
	updateCategoryIndices,
	validateCategoryName,
	validateFormEditLink,
	VIRTUAL_ALL_PROFILE_ID,
	verifyParamsUpdateLink,
	includesProfile,
	removeProfileFromScope
} from '../../tools';
import {deleteCategory, deleteLink, updateCategory, updateLink} from "../../firebaseTools";

import EditCategory from "../../components/EditCategory";
import EditLink from "../../components/EditLink";
import {StateContext} from "../../App";

const cloneIdxPerProfileMap = (raw) => {
	if (raw != null && typeof raw === 'object') {
		return {...raw};
	}
	return {};
};

const mergeRenamedLinksIntoPreviousState = (prev, renamedLinks) =>
	prev.map((link) => renamedLinks.find((item) => item.id === link.id) ?? link);

async function renameLinksAfterCategoryNameChange({
	categoryId,
	nextCategoryName,
	allLinks,
	addError,
	setAllLinks
}) {
	const linksToRename = allLinks.filter((item) => item.categoryId === categoryId);
	const renamedLinks = [];
	for (const link of linksToRename) {
		const nextLink = {...link, category: nextCategoryName};
		await updateLink(link.id, {category: nextCategoryName}, addError);
		renamedLinks.push(nextLink);
	}

	if (renamedLinks.length) {
		setAllLinks((prev) => mergeRenamedLinksIntoPreviousState(prev, renamedLinks));
	}
}

const DROP_ANIMATION = {
	duration: 280,
	easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
};

const SortableCategoryCard = ({id, isDragEnabled, children}) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({id, disabled: !isDragEnabled});

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0 : 1,
	};

	return (
		<div
			ref={setNodeRef}
			style={style}
			className="category-container"
		>
			{children({
				headerDragProps: isDragEnabled ? {...listeners, ...attributes} : null,
			})}
		</div>
	);
};

SortableCategoryCard.propTypes = {
	id: PropTypes.string.isRequired,
	isDragEnabled: PropTypes.bool.isRequired,
	children: PropTypes.func.isRequired,
};

const SKELETON_CARD_COUNT = 5;
const SKELETON_ROWS_PER_CARD = 3;

const Body = ({isEdit, categories, links, activeProfile, addError}) => {
	const {
		profiles,
		allCategories,
		setAllCategories,
		allLinks,
		setAllLinks,
		isDataLoading,
	} = useContext(StateContext);

	const [editingCategoryObject, setEditingCategoryObject] = useState(null);
	const [editingLinkObject, setEditingLinkObject] = useState(null);
	const [searchTerm, setSearchTerm] = useState("");
	const [collapsed, setCollapsed] = useState({});
	const [activeDragId, setActiveDragId] = useState(null);
	const [dragOrder, setDragOrder] = useState(null);
	const dragStartOrderRef = useRef(null);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {distance: 8},
		})
	);

	const handleCloseModal = () => {
		setEditingCategoryObject(null);
		setEditingLinkObject(null);
	};

	const refreshCategoryMembership = async (categoryId, linksState) => {
		const category = allCategories.find((item) => item.id === categoryId);
		if (!category) return;

		const linksByCategory = linksState.filter((item) => item.categoryId === categoryId);
		if (linksByCategory.length === 0) {
			await deleteCategory(categoryId, addError);
			setAllCategories((prev) => prev.filter((item) => item.id !== categoryId));
			return;
		}

		const anyLinkHasAllScope = linksByCategory.some((item) =>
			(item.profileIds ?? []).includes(VIRTUAL_ALL_PROFILE_ID)
		);

		let nextProfileIds;
		let nextIdxPerProfile = cloneIdxPerProfileMap(category.idxPerProfile);

		if (anyLinkHasAllScope) {
			nextProfileIds = [VIRTUAL_ALL_PROFILE_ID];
			delete nextIdxPerProfile[VIRTUAL_ALL_PROFILE_ID];
		} else {
			nextProfileIds = Array.from(new Set(
				linksByCategory.flatMap((item) => item.profileIds ?? [])
			)).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID);

			Object.keys(nextIdxPerProfile).forEach((profileId) => {
				if (!nextProfileIds.includes(profileId)) {
					delete nextIdxPerProfile[profileId];
				}
			});

			nextProfileIds.forEach((profileId) => {
				if (nextIdxPerProfile[profileId] == null) {
					nextIdxPerProfile[profileId] = calculateNextIdxForProfile(profileId, allCategories);
				}
			});
		}

		const updatedCategory = {
			...category,
			profileIds: nextProfileIds,
			idxPerProfile: nextIdxPerProfile,
		};
		await updateCategory(updatedCategory, addError);
		setAllCategories((prev) => prev.map((item) => item.id === updatedCategory.id ? updatedCategory : item));
	};

	const categoriesToDisplay = useMemo(() => {
		if (dragOrder) {
			const byId = Object.fromEntries(categories.filter((c) => c.id).map((c) => [c.id, c]));
			const reordered = dragOrder.map((id) => byId[id]).filter(Boolean);
			return padCategories(reordered);
		}
		return sortCategories(padCategories(categories));
	}, [categories, dragOrder]);

	const linksByCategory = useMemo(() => {
		if (!links || !Array.isArray(links)) return {};
		return getLinksByCategories(categoriesToDisplay, links);
	}, [links, categoriesToDisplay]);

	const {filteredCategories, filteredLinksByCategory} = useMemo(() => {
		const normalizedLinks = Array.isArray(links) ? links : [];
		const term = searchTerm.trim().toLowerCase();
		if (term.length < 2) {
			return {
				filteredCategories: categoriesToDisplay,
				filteredLinksByCategory: linksByCategory,
			};
		}

		const matches = (value) =>
			String(value ?? '')
				.toLowerCase()
				.split(/\s+/)
				.some((w) => w.startsWith(term));

		const matchingLinksByCategory = normalizedLinks.reduce((acc, link) => {
			if (!matches(link.name)) {
				return acc;
			}

			if (!acc[link.category]) {
				acc[link.category] = [];
			}
			acc[link.category].push(link);
			return acc;
		}, {});

		const filteredCats = [];
		const filteredLinksCatMap = {};

		for (const cat of categoriesToDisplay) {
			const catKey = cat.category;

			if (matches(catKey)) {
				filteredCats.push(cat);
				filteredLinksCatMap[catKey] = linksByCategory[catKey] || [];
			} else {
				const sub = matchingLinksByCategory[catKey] || [];
				if (sub.length) {
					filteredCats.push(cat);
					filteredLinksCatMap[catKey] = sub;
				}
			}
		}

		return {
			filteredCategories: filteredCats,
			filteredLinksByCategory: filteredLinksCatMap,
		};
	}, [searchTerm, categoriesToDisplay, linksByCategory, links]);

	const isDragEnabled = isEdit && searchTerm.trim().length < 2;

	const sortableIds = useMemo(
		() => categoriesToDisplay.filter((c) => c.id).map((c) => c.id),
		[categoriesToDisplay]
	);

	const activeSortableIds = dragOrder ?? sortableIds;

	const handleDragStart = ({active}) => {
		setActiveDragId(active.id);
		dragStartOrderRef.current = sortableIds;
		setDragOrder(sortableIds);
	};

	const handleDragOver = ({active, over}) => {
		if (!over || active.id === over.id) return;
		setDragOrder((prev) => {
			const ids = prev ?? sortableIds;
			const oldIndex = ids.indexOf(active.id);
			const newIndex = ids.indexOf(over.id);
			if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
			return arrayMove(ids, oldIndex, newIndex);
		});
	};

	const handleDragEnd = async ({active}) => {
		setActiveDragId(null);

		const initialOrder = dragStartOrderRef.current;
		dragStartOrderRef.current = null;

		if (!dragOrder || !initialOrder) {
			setDragOrder(null);
			return;
		}

		const newPos = dragOrder.indexOf(active.id);
		const oldPos = initialOrder.indexOf(active.id);

		if (newPos === -1 || oldPos === -1 || newPos === oldPos) {
			setDragOrder(null);
			return;
		}

		const profileId = activeProfile?.id ?? VIRTUAL_ALL_PROFILE_ID;
		const activeCategory = allCategories.find((c) => c.id === active.id);
		const overCategoryId = initialOrder[newPos];
		const overCategory = allCategories.find((c) => c.id === overCategoryId);

		if (!activeCategory || !overCategory) {
			setDragOrder(null);
			return;
		}

		const fromIdx = getCategoryIdxForProfile(activeCategory, profileId);
		const toIdx = getCategoryIdxForProfile(overCategory, profileId);

		if (fromIdx === toIdx) {
			setDragOrder(null);
			return;
		}

		const updated = await updateCategoryIndices(
			allCategories,
			active.id,
			toIdx,
			fromIdx,
			addError,
			profileId
		);
		setAllCategories(updated);
		setDragOrder(null);
	};

	const activeDragCategory = activeDragId
		? categoriesToDisplay.find((c) => c.id === activeDragId)
		: null;

	const handleUpdateLink = (linkInput) => {
		const link = {
			...linkInput,
			profileIds: Array.isArray(linkInput.profileIds) ? linkInput.profileIds : []
		};
		if (!validateFormEditLink(link, addError)) {
			return;
		}

		const linkId = link.id;
		const oldLink = allLinks.find((l) => l.id === linkId);
		if (!oldLink) return;

		const category = getFormattedValue(link.category);

		let optimisticImage = oldLink.image;
		let optimisticBlobUrl = null;
		if (link.image instanceof File) {
			optimisticBlobUrl = URL.createObjectURL(link.image);
			optimisticImage = optimisticBlobUrl;
		} else if (typeof link.image === 'string' && link.image) {
			optimisticImage = link.image;
		}

		const previousAllLinks = [...allLinks];

		setAllLinks((prev) => prev.map((l) =>
			l.id === linkId
				? {...l, name: link.name, url: link.url, category, image: optimisticImage, profileIds: link.profileIds}
				: l
		));
		handleCloseModal();

		(async () => {
			try {
				const downloadUrl = await verifyParamsUpdateLink(link, addError);
				if (downloadUrl === null) {
					throw new Error();
				}

				const categoryId = await handleAddCategory({
					allCategories,
					setAllCategories,
					categories,
					category,
					profileIds: link.profileIds,
					addError
				});
				if (categoryId == null) {
					addError("Failed to add category");
					throw new Error();
				}

				const linkData = {
					categoryId,
					category,
					name: link.name,
					url: link.url,
					image: downloadUrl,
					profileIds: link.profileIds
				};

				await updateLink(linkId, linkData, addError);

				setAllLinks((prev) => prev.map((l) => l.id === linkId ? {...l, ...linkData} : l));

				const confirmedLinks = allLinks.map((l) => l.id === linkId ? {...l, ...linkData} : l);
				if (oldLink.categoryId && oldLink.categoryId !== categoryId) {
					await refreshCategoryMembership(oldLink.categoryId, confirmedLinks);
				}
				await refreshCategoryMembership(categoryId, confirmedLinks);
			} catch {
				setAllLinks(previousAllLinks);
			} finally {
				if (optimisticBlobUrl) {
					URL.revokeObjectURL(optimisticBlobUrl);
				}
			}
		})();
	};

	const handleRemoveLink = (linkToRemove) => {
		if (!globalThis.window.confirm(`Are you sure you want to delete "${linkToRemove.name}"?`)) {
			return;
		}

		const profileId = activeProfile?.id;
		const isAll = profileId === VIRTUAL_ALL_PROFILE_ID;
		const existing = allLinks.find((item) => item.id === linkToRemove.id);
		if (!existing) return;

		const remainingProfileIds = isAll
			? []
			: removeProfileFromScope(existing.profileIds, profileId, profiles);
		const shouldHardDelete = isAll || remainingProfileIds.length === 0;

		const nextLinks = shouldHardDelete
			? allLinks.filter((item) => item.id !== existing.id)
			: allLinks.map((item) =>
				item.id === existing.id ? {...item, profileIds: remainingProfileIds} : item
			);

		const previousAllLinks = [...allLinks];

		setAllLinks(nextLinks);
		handleCloseModal();

		(async () => {
			try {
				if (shouldHardDelete) {
					const removed = await deleteLink(existing.id, addError);
					if (!removed) throw new Error();
				} else {
					await updateLink(existing.id, {profileIds: remainingProfileIds}, addError);
				}
				await refreshCategoryMembership(existing.categoryId, nextLinks);
			} catch {
				setAllLinks(previousAllLinks);
			}
		})();
	};

	const handleRemoveCategory = async (categoryData) => {
		if (!globalThis.window.confirm(`Are you sure you want to delete the category "${categoryData.category}"?`)) {
			return;
		}

		const categoryId = categoryData?.id;
		if (!categoryId) {
			addError("Invalid category data for removal");
			return;
		}

		const linksInCategory = allLinks.filter((link) => link.categoryId === categoryId);
		const isAll = activeProfile?.id === VIRTUAL_ALL_PROFILE_ID;
		let nextLinks = [...allLinks];

		for (const link of linksInCategory) {
			if (isAll) {
				await deleteLink(link.id, addError);
				nextLinks = nextLinks.filter((item) => item.id !== link.id);
				continue;
			}

			if (!includesProfile(link, activeProfile.id)) {
				continue;
			}

			const remainingProfileIds = removeProfileFromScope(link.profileIds, activeProfile.id, profiles);
			if (remainingProfileIds.length === 0) {
				await deleteLink(link.id, addError);
				nextLinks = nextLinks.filter((item) => item.id !== link.id);
			} else {
				await updateLink(link.id, {profileIds: remainingProfileIds}, addError);
				nextLinks = nextLinks.map((item) =>
					item.id === link.id ? {...item, profileIds: remainingProfileIds} : item
				);
			}
		}

		setAllLinks(nextLinks);
		await refreshCategoryMembership(categoryId, nextLinks);
		handleCloseModal();
	};

	const handleSaveCategory = async (categoryData) => {
		const category = allCategories.find((item) => item.id === categoryData?.id);
		if (!category) {
			addError("Invalid category data for update");
			return;
		}

		if (!validateCategoryName(categoryData.category, addError)) {
			return;
		}

		const oldCategoryName = category.category;
		const nextCategoryName = formatCategoryName(categoryData.category);

		let nextCategoriesState = allCategories;

		const reorderedCategory = nextCategoriesState.find((item) => item.id === category.id) ?? category;
		const targetCategory = findCategoryRenameCollisionTarget(
			nextCategoriesState,
			reorderedCategory.id,
			nextCategoryName
		);
		const isRename = normalizeCategoryNameForComparison(oldCategoryName)
			!== normalizeCategoryNameForComparison(nextCategoryName);

		if (isRename && targetCategory) {
			const merged = await mergeCategoryOnRenameCollision({
				nextCategoryName,
				nextCategoriesState,
				sourceCategory: reorderedCategory,
				targetCategory,
				allLinks,
				addError
			});
			setAllCategories(merged.categories);
			setAllLinks(merged.links);
			handleCloseModal();
			return;
		}

		const renamedCategory = {
			...reorderedCategory,
			category: nextCategoryName,
			profileIds: (reorderedCategory.profileIds ?? []).filter((id) => id && id !== VIRTUAL_ALL_PROFILE_ID),
		};

		if (isRename) {
			await updateCategory(renamedCategory, addError);
			nextCategoriesState = nextCategoriesState.map((item) =>
				item.id === renamedCategory.id ? renamedCategory : item
			);
		}

		setAllCategories(nextCategoriesState);

		if (isRename) {
			await renameLinksAfterCategoryNameChange({
				categoryId: category.id,
				nextCategoryName,
				allLinks,
				addError,
				setAllLinks
			});
		}

		handleCloseModal();
	};

	const renderCategoryContent = (categoryObj, catIndex, {headerDragProps} = {}) => {
		return (
			<>
				<div
					className={`category-header${isDragEnabled && headerDragProps ? ' category-header--draggable' : ''}`}
					{...(headerDragProps ?? {})}
				>
				<h2
					className={`category-title${isEdit ? ' category-title--editable' : ''}`}
					{...(isEdit ? {
						onClick: (e) => {
							e.stopPropagation();
							setEditingCategoryObject(categoryObj);
						}
					} : {})}
				>
					{categoryObj.category}
				</h2>
				{!isEdit &&
					<FontAwesomeIcon
						icon={faCaretDown}
						className="collapse-icon"
						onClick={() => {
							setCollapsed(prev => ({
								...prev,
								[categoryObj.category]: !prev[categoryObj.category]
							}));
						}}
					/>
				}
				</div>
				{(collapsed[categoryObj.category]
					? []
					: filteredLinksByCategory[categoryObj.category] || []
				).map((link, linkIndex) => {
					const isFirstLink = catIndex === 0 && linkIndex === 0;
					const imgProps = {
						src: getLinkImageDisplayUrl(link.image),
						className: "link-image",
						alt: "",
						loading: isFirstLink ? "eager" : "lazy",
						decoding: "async",
						...(isFirstLink && {fetchPriority: "high"}),
					};
					return isEdit ? (
						<button
							type="button"
							className="link-container"
							key={link.id}
							onClick={(e) => {
								e.preventDefault();
								setEditingLinkObject(link);
							}}
						>
							<span className="link">
								<img {...imgProps} />
								<span className="link-text">{link.name}</span>
							</span>
						</button>
					) : (
						<div className="link-container" key={link.id}>
							{isSafeLinkUrl(link.url) ? (
								<a className="link" rel="noopener noreferrer" href={link.url} target="_blank">
									<img {...imgProps} />
									<span className="link-text">{link.name}</span>
								</a>
							) : (
								<span className="link link--unsafe" title="This link has an unsupported URL">
									<img {...imgProps} />
									<span className="link-text">{link.name}</span>
								</span>
							)}
						</div>
					);
				})}
			</>
		);
	};

	return (
		<>
			<div className="grid-wrapper">
				<div className="grid-header">
					<input className="search-input"
					       type="text"
					       value={searchTerm}
					       onChange={(e) => setSearchTerm(e.target.value)}
					       placeholder="Search categories or links..."
					/>
				</div>
				{isDataLoading ? (
					<div
						className="categories-grid"
						aria-busy="true"
						aria-live="polite"
						aria-label="Loading your links"
					>
						{Array.from({length: SKELETON_CARD_COUNT}, (_, i) => (
							<div key={i} className="category-container skeleton-category-container" aria-hidden="true">
								<div className="skeleton-category-header" />
								{Array.from({length: SKELETON_ROWS_PER_CARD}, (_, j) => (
									<div key={j} className="skeleton-link-row" />
								))}
							</div>
						))}
					</div>
				) : (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragStart={handleDragStart}
						onDragOver={handleDragOver}
						onDragEnd={handleDragEnd}
						onDragCancel={() => { dragStartOrderRef.current = null; setActiveDragId(null); setDragOrder(null); }}
					>
						<SortableContext items={activeSortableIds} strategy={rectSortingStrategy}>
							<div className={`categories-grid${activeDragId ? ' categories-grid--dragging' : ''}`}>
								{filteredCategories.map((categoryObj, catIndex) => {
									if (!categoryObj.id) {
										return (
											<div
												key={`empty-category-${catIndex}`}
												className="category-container empty-category"
											/>
										);
									}
									return (
										<SortableCategoryCard
											key={categoryObj.id}
											id={categoryObj.id}
											isDragEnabled={isDragEnabled}
										>
											{(dragProps) => renderCategoryContent(categoryObj, catIndex, dragProps)}
										</SortableCategoryCard>
									);
								})}
							</div>
						</SortableContext>
						<DragOverlay dropAnimation={DROP_ANIMATION}>
							{activeDragCategory ? (
								<div className="category-container category-container--dragging">
									{renderCategoryContent(activeDragCategory, -1)}
								</div>
							) : null}
						</DragOverlay>
					</DndContext>
				)}
			</div>
			{editingCategoryObject && (
				<EditCategory
					categories={categories}
					categoryObject={editingCategoryObject}
					onClose={handleCloseModal}
					onSave={handleSaveCategory}
					onRemove={handleRemoveCategory}
				/>
			)}

			{editingLinkObject && (
				<EditLink
					links={allLinks}
					categories={categories}
					profiles={profiles}
					activeProfile={activeProfile}
					link={editingLinkObject}
					onClose={handleCloseModal}
					onSave={handleUpdateLink}
					onRemove={handleRemoveLink}
				/>
			)}
		</>
	);
};

Body.propTypes = {
	isEdit: PropTypes.bool.isRequired,
	categories: PropTypes.array.isRequired,
	links: PropTypes.array.isRequired,
	activeProfile: PropTypes.shape({
		id: PropTypes.string
	}),
	addError: PropTypes.func.isRequired,
};

export default Body;
